/**
 * EditBankDialog — wires the existing PUT /question-banks/{id} endpoint.
 *
 * Pre-fills the form from the live bank record, lets the trainer edit
 * title / description / tags / default_language / client_id, and PATCHes
 * the diff on save. Title is the only required field (mirrors create flow).
 *
 * Tag input is the same "comma-separated string" UX as CreateBankDialog so
 * the two dialogs feel like a pair.
 */
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useApi } from '../../hooks/useApi';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Loader2, Save } from 'lucide-react';

const tagsArrayToString = (arr) => (arr || []).join(', ');

export default function EditBankDialog({ open, onClose, bank, onSaved }) {
  const { put } = useApi();
  const [form, setForm] = useState({
    title: '',
    description: '',
    default_language: 'en',
    client_id: '',
    tags: '',
  });
  const [saving, setSaving] = useState(false);

  // Re-seed every time the dialog opens — handles the "reopen on a different
  // bank" case cleanly without stale form state from a prior edit session.
  useEffect(() => {
    if (open && bank) {
      setForm({
        title: bank.title || '',
        description: bank.description || '',
        default_language: bank.default_language || 'en',
        client_id: bank.client_id || '',
        tags: tagsArrayToString(bank.tags),
      });
    }
  }, [open, bank]);

  const update = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const canSave = form.title.trim().length > 0;

  const handleSave = async () => {
    if (!bank) return;
    setSaving(true);
    try {
      const tagsArr = form.tags
        .split(',').map(t => t.trim()).filter(Boolean).slice(0, 20);
      // Send only fields that actually changed — PUT here accepts all-optional
      // fields and only mutates the ones present in the payload.
      const payload = {};
      if (form.title.trim() !== (bank.title || '')) payload.title = form.title.trim();
      if (form.description.trim() !== (bank.description || '')) payload.description = form.description.trim();
      if (form.default_language !== (bank.default_language || 'en')) payload.default_language = form.default_language;
      if (form.client_id.trim() !== (bank.client_id || '')) payload.client_id = form.client_id.trim();
      const currentTags = tagsArrayToString(bank.tags);
      if (form.tags !== currentTags) payload.tags = tagsArr;

      if (Object.keys(payload).length === 0) {
        toast.info('Nothing changed');
        onClose();
        return;
      }

      const updated = await put(`/question-banks/${bank.bank_id}`, payload);
      toast.success('Bank updated');
      onSaved?.(updated);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="edit-bank-dialog">
        <DialogHeader>
          <DialogTitle>Edit bank</DialogTitle>
          <DialogDescription className="text-xs">
            Changes apply immediately. Existing quiz attempts that reference items in this bank are unaffected.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="eb-title">Title <span className="text-red-500">*</span></Label>
            <Input
              id="eb-title"
              data-testid="eb-title"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
              maxLength={200}
              disabled={saving}
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="eb-description">Description</Label>
            <Textarea
              id="eb-description"
              data-testid="eb-description"
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              rows={3}
              maxLength={2000}
              disabled={saving}
              placeholder="Short summary — what kind of questions live here?"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="eb-lang">Default language</Label>
              <Select
                value={form.default_language}
                onValueChange={(v) => update({ default_language: v })}
                disabled={saving}
              >
                <SelectTrigger id="eb-lang" data-testid="eb-lang"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="en">English</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="eb-client">Client ID</Label>
              <Input
                id="eb-client"
                data-testid="eb-client"
                value={form.client_id}
                onChange={(e) => update({ client_id: e.target.value })}
                placeholder="optional"
                maxLength={100}
                disabled={saving}
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="eb-tags">Tags <span className="text-muted-foreground text-xs">(comma-separated)</span></Label>
            <Input
              id="eb-tags"
              data-testid="eb-tags"
              value={form.tags}
              onChange={(e) => update({ tags: e.target.value })}
              placeholder="onboarding, compliance, vendor-X"
              disabled={saving}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button
            onClick={handleSave}
            disabled={!canSave || saving}
            data-testid="eb-save"
            className="bg-[var(--lumyn-banks)] hover:bg-[var(--lumyn-banks)]/90 text-white"
          >
            {saving ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
